import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Newspaper, ExternalLink, RefreshCw, Sparkles, Pin } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso);
    const diff = (Date.now() - then.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return then.toLocaleDateString();
  } catch {
    return "";
  }
}

function NewsItem({ item, pinned = false }) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block p-3 rounded-md transition-colors hover:bg-yellow-50"
      style={{ border: "2px solid #0F1B2D", background: pinned ? "#FFFBEB" : "white" }}
      data-testid={pinned ? "news-pick-item" : "news-feed-item"}
    >
      <div className="flex items-start gap-3">
        {pinned && (
          <Pin size={14} className="flex-shrink-0 mt-1 rm-text-orange" />
        )}
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm leading-snug line-clamp-2">
            {item.title}
          </div>
          {item.summary && (
            <div className="text-xs mt-1 line-clamp-2" style={{ color: "#475569" }}>
              {item.summary}
            </div>
          )}
          <div className="flex items-center gap-2 mt-2 font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#475569" }}>
            <span className="rm-text-orange font-bold">{item.source}</span>
            {item.published && (
              <>
                <span>·</span>
                <span>{timeAgo(item.published) || item.published.split(" ").slice(0, 3).join(" ")}</span>
              </>
            )}
            <ExternalLink size={10} className="ml-auto" />
          </div>
        </div>
      </div>
    </a>
  );
}

export default function NewsFeed() {
  const [feed, setFeed] = useState([]);
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [feedRes, picksRes] = await Promise.all([
        api.get("/news/feed"),
        api.get("/news/picks").catch(() => ({ data: [] })),
      ]);
      setFeed(feedRes.data?.items || []);
      setPicks(picksRes.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div
      className="rm-card-static p-6 md:p-7"
      style={{ background: "#FFFBEB" }}
      data-testid="news-feed-card"
    >
      <div className="flex items-start gap-3 mb-4">
        <div
          className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: "#0F1B2D", color: "white", border: "3px solid #0F1B2D" }}
        >
          <Newspaper size={22} strokeWidth={2.5} />
        </div>
        <div className="flex-1">
          <div className="rm-tag mb-1">Live</div>
          <h2 className="font-display text-2xl md:text-3xl uppercase leading-tight">
            Job market <span className="rm-text-orange">pulse.</span>
          </h2>
          <div className="font-mono-rm text-xs uppercase tracking-widest mt-1" style={{ color: "#475569" }}>
            Aggregated from BLS, Indeed Hiring Lab, Layoffs.fyi, LinkedIn Talent, Built In, CNBC, HBR
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="rm-btn rm-btn-ghost text-xs p-2"
          data-testid="news-refresh-btn"
          aria-label="Refresh"
          title="Refresh"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {error && (
        <div className="p-3 mb-3 rounded-md font-mono-rm text-sm" style={{ background: "#FEF2F2", border: "2px solid #EF4444", color: "#991B1B" }}>
          {error}
        </div>
      )}

      {/* Editor's Picks */}
      {picks.length > 0 && (
        <div className="mb-4" data-testid="news-picks-section">
          <div className="rm-label mb-2 flex items-center gap-1">
            <Sparkles size={12} /> Editor's pick
          </div>
          <div className="space-y-2">
            {picks.map((p) => (
              <NewsItem key={p.id} item={p} pinned />
            ))}
          </div>
        </div>
      )}

      {/* Live RSS feed */}
      <div className="rm-label mb-2">Latest</div>
      {loading && feed.length === 0 ? (
        <div className="text-center py-6 font-mono-rm text-sm" style={{ color: "#475569" }}>
          Fetching the pulse…
        </div>
      ) : feed.length === 0 ? (
        <div className="text-center py-6 font-mono-rm text-sm" style={{ color: "#475569" }}>
          No items right now — try refreshing in a moment.
        </div>
      ) : (
        <div
          className="space-y-2 overflow-y-auto pr-1"
          style={{ maxHeight: "26rem" }}
          data-testid="news-feed-list"
        >
          {feed.map((item, i) => (
            <NewsItem key={item.url || i} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
